/**
 * Yazıcı adresi yardımcıları — görev eki / SP'nin yazıcı alanı için.
 *
 * Adres DAL'daki çözümlemeyle aynı dili konuşur (DevicePrintService.ResolveAsync):
 * takma ad (`DEPO-ETIKET`), `CIHAZ/Yazıcı Adı` ya da `@local`. Takma ad varsa adres odur
 * (kısa ve cihaz değişse de kalıcı); yoksa cihaz kodu + yazıcı adı. Listede olmayan bir ad
 * sunucu yazıcısıdır: Worker onu kendi makinesinde basar.
 */
import type { SearchOption } from '@/shared/ui/optionSearch';
import type { DeviceDto, DevicePrinterDto } from '../api/deviceApi';

export interface PrinterMatch {
  device: DeviceDto;
  printer: DevicePrinterDto;
}

/** Bir cihaz yazıcısının adres metni: takma ad, yoksa CIHAZ/Yazıcı. */
export function printerAddressOf(device: DeviceDto, printer: DevicePrinterDto): string {
  return printer.printerAlias?.trim() ? printer.printerAlias.trim() : `${device.deviceCode}/${printer.printerName}`;
}

/** Seçim kutusu seçenekleri: her cihazın aktif yazıcıları; pasif/çevrimdışı cihaz işaretli. */
export function printerOptions(devices: DeviceDto[] | undefined, labels: { offline: string }): SearchOption[] {
  const out: SearchOption[] = [];
  for (const d of devices ?? []) {
    if (!d.active) continue;
    for (const p of d.printers ?? []) {
      if (p.active === false) continue;
      const address = printerAddressOf(d, p);
      const detail = p.printerAlias?.trim() ? `${d.deviceCode} / ${p.printerName}` : null;
      out.push({
        id: address,
        name: `${address}${detail ? ` · ${detail}` : ''}${d.online ? '' : ` · ${labels.offline}`}`,
      });
    }
  }
  return out;
}

/**
 * Yazılı adres hangi cihaz yazıcısına gidiyor? DAL'daki sırayla: takma ad, CIHAZ/Yazıcı.
 * `@local` tarayıcının bulunduğu PC'ye bağlı, Worker görevinde anlamı yok → null.
 * Hiçbiri tutmazsa null: sunucu yazıcısı.
 */
export function resolvePrinterAddress(printerName: string | null | undefined, devices: DeviceDto[] | undefined): PrinterMatch | null {
  const name = (printerName ?? '').trim();
  if (!name || !devices?.length) return null;
  const lower = name.toLowerCase();
  for (const d of devices)
    for (const p of d.printers ?? []) if (p.printerAlias && p.printerAlias.trim().toLowerCase() === lower) return { device: d, printer: p };
  const sep = name.search(/[/\\]/);
  if (sep > 0 && sep < name.length - 1) {
    const code = name.slice(0, sep).trim().toLowerCase();
    const pname = name.slice(sep + 1).trim().toLowerCase();
    for (const d of devices) {
      if (d.deviceCode.toLowerCase() !== code) continue;
      for (const p of d.printers ?? []) if (p.printerName.toLowerCase() === pname) return { device: d, printer: p };
    }
  }
  return null;
}
